import { ComponentType, lazy, LazyExoticComponent } from "react";

/**
 * Handles the "Failed to fetch dynamically imported module" class of errors.
 *
 * Vite emits every lazy chunk with a content hash in its filename
 * (e.g. `DashBoardTodaysTasks-a0963063.js`). When a new build is deployed the
 * old hashed files are removed from the server, but any browser tab that is
 * still running the previous `index.html` keeps pointing at them. The moment
 * the user hits a `lazy()` boundary the import 404s and the whole route dies
 * inside the ErrorBoundary.
 *
 * The two failure modes need different treatment:
 *  - a transient network/CDN blip -> retrying the same import usually works.
 *  - a stale deploy -> the file is really gone, the only cure is reloading the
 *    document so the browser picks up the new `index.html` and its new hashes.
 *
 * @author Rajkumar Rathod
 */

/** sessionStorage key holding the timestamp of the last forced reload. */
const RELOAD_KEY = "ech:chunk-reload-at";

/**
 * Skip force-reloading again within this window so a chunk that is genuinely
 * broken on the server can never put the tab into a reload loop. The second
 * failure falls through to the ErrorBoundary instead.
 */
const RELOAD_COOLDOWN_MS = 15_000;

/**
 * Delay before each retry attempt. Length also decides the retry count.
 *
 * Deliberately short and shallow: when the cause is a stale deploy the server
 * answers deterministically (our S3/CloudFront setup serves `index.html` with
 * a 200 for missing assets, so the browser rejects it as a bad MIME type every
 * single time). Retrying cannot fix that, it only delays the reload that can.
 * The one retry is there for genuine network blips and for the brief window
 * where CDN edges disagree about which build they have.
 */
const RETRY_DELAYS_MS = [300];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** sessionStorage throws in some privacy modes — never let that break a route. */
const readReloadStamp = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeReloadStamp = (value: number): void => {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(value));
  } catch {
    /* ignore — worst case we lose the loop guard */
  }
};

/**
 * Detects the browser-specific wording for "I could not download that module".
 * Chrome, Firefox and Safari all phrase it differently.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  let message = "";

  if (typeof error === "string") message = error;
  else if (error instanceof Error) message = error.message;
  /** some browsers reject with a plain object / ErrorEvent rather than an Error */
  else if (
    error &&
    typeof error === "object" &&
    typeof (error as { message?: unknown }).message === "string"
  )
    message = (error as { message: string }).message;

  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /'text\/html' is not a valid javascript mime type/i.test(message) ||
    /chunkloaderror/i.test(message)
  );
};

/**
 * Reloads the document once so the browser refetches `index.html` and the
 * asset hashes it references.
 *
 * @returns `true` when a reload was triggered, `false` when it was suppressed
 * by the cooldown (i.e. reloading already failed to fix it).
 */
export const reloadForFreshChunks = (): boolean => {
  const now = Date.now();

  if (now - readReloadStamp() < RELOAD_COOLDOWN_MS) return false;

  writeReloadStamp(now);
  window.location.reload();
  return true;
};

/**
 * Runs a dynamic `import()` with retries, then falls back to a hard reload.
 *
 * Usable on its own for non-component dynamic imports:
 * @example
 * const module = await retryDynamicImport(() => import("some-heavy-lib"));
 */
const retryDynamicImport = async <T>(
  factory: () => Promise<T>
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;

      /** a real bug inside the module — retrying would only hide it */
      if (!isChunkLoadError(error)) throw error;

      const delay = RETRY_DELAYS_MS[attempt];
      if (delay !== undefined) await wait(delay);
    }
  }

  /**
   * Every attempt 404'd, so this is a stale deploy rather than a flaky
   * network. Reload to pick up the current build. The promise is left pending
   * on purpose: resolving/rejecting it would flash the error page during the
   * moment it takes the browser to navigate away.
   */
  if (reloadForFreshChunks()) {
    return new Promise<T>(() => {});
  }

  /** already reloaded once and it did not help — let the ErrorBoundary show */
  throw lastError;
};

/**
 * Drop-in replacement for `React.lazy` that survives a mid-session deploy.
 *
 * @example
 * const DashBoardTodaysTasks = lazyWithRetry(() => import("./DashBoardTodaysTasks"));
 */
const lazyWithRetry = <T extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own signature
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> => lazy(() => retryDynamicImport(factory));

export default lazyWithRetry;
